// src/App.jsx
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Canvas } from "@react-three/fiber";
import { useProgress } from "@react-three/drei";

import StripeMask from "./StripeMask.jsx";
import ParallaxTextPunchout from "./ParallaxTextPunchout";
import MainScene from "./MainMenu.jsx";
import AboutMe from "./AboutMe.jsx";

import "./Loading.css";
import "./App.css";

const BASE = import.meta.env.BASE_URL

/* -----------------------------------------------------------------------------
 * Constants / config
 * -------------------------------------------------------------------------- */

const FOCUS_BLURBS = {
  1: {
    side: "right",
    title: "Cybos",
    body: "A web-based virtual pet sim game that has a small but dedicated userbase.",
    link: "https://cybos.app/",
  },
  2: {
    side: "left",
    title: "Ganbaru 頑張る",
    body: "A site tailored towards investors. It shows off the features of the upcoming gambling platform, in a concise and unique way.",
    link: "https://www.explorar.art/invest",
  },
  3: {
    side: "right",
    title: "Biteback Studios",
    body: "Landing page created for an up-and-coming video game studio. Intended to showcase the company's off-the-wall style and mold breaking approach to game design.",
    link: "https://biteback.neocities.org/",
  },
};

const MODE_ORDER_RIGHT = { main: "alt", alt: "main" };
const MODE_ORDER_LEFT = { main: "alt", alt: "main" };

/* -----------------------------------------------------------------------------
 * Helpers
 * -------------------------------------------------------------------------- */

function clamp(n, min, max) {
  return Math.max(min, Math.min(max, n));
}

function toNormPointer(clientX, clientY, w, h) {
  const x = (clientX / (w || 1) - 0.5) * 2;
  const y = (clientY / (h || 1) - 0.5) * 2;
  return { x, y };
}

/**
 * ABOUT card “repulsion + tilt” (pure function => easier to reason about/test).
 * Keeps behavior aligned with your current math.
 */
function computeAboutCardTransform(pointer, viewport) {
  const w = viewport?.w ?? (typeof window !== "undefined" ? window.innerWidth : 1);
  const h = viewport?.h ?? (typeof window !== "undefined" ? window.innerHeight : 1);

  // current behavior: card “base” is fixed at 40%/40% of viewport
  const cardBaseX = w * 0.4;
  const cardBaseY = h * 0.4;

  const dx = cardBaseX - pointer.x;
  const dy = cardBaseY - pointer.y;

  const dist = Math.sqrt(dx * dx + dy * dy) || 1;
  const maxRadius = 600;

  const clampedDist = Math.min(dist, maxRadius);
  const strength = 1 - clampedDist / maxRadius;

  const dirX = dx / dist;
  const dirY = dy / dist;

  const cardIntensity = { x: 40, y: 28 };
  const offsetX = dirX * strength * cardIntensity.x;
  const offsetY = dirY * strength * cardIntensity.y;

  const maxTiltPx = 280;
  const tiltMax = 14;

  const txNorm = clamp(dx / maxTiltPx, -1, 1);
  const tyNorm = clamp(dy / maxTiltPx, -1, 1);

  const tiltX = tyNorm * tiltMax;
  const tiltY = -txNorm * tiltMax;

  return { offsetX, offsetY, tiltX, tiltY };
}

/* -----------------------------------------------------------------------------
 * Scene wrapper with loading progress
 * -------------------------------------------------------------------------- */

function MainSceneWithProgress({ onReady, onProgress, ...props }) {
  const { active, progress, loaded, total } = useProgress();

  useEffect(() => {
    onProgress?.(progress);

    // Only mark ready when something registered to load and it finished.
    if (!active && total > 0 && loaded === total) {
      onReady?.();
    }
  }, [active, progress, loaded, total, onReady, onProgress]);

  return <MainScene {...props} />;
}

/* -----------------------------------------------------------------------------
 * App
 * -------------------------------------------------------------------------- */

export default function App() {
  // refs used by MainScene camera targeting
  const spriteFocusRef = useRef();
  const chipFocusRef = useRef();
  const biteFocusRef = useRef();
  const sceneGroupRef = useRef();

  // core UI state
  const [mode, setMode] = useState("main"); // 'main' | 'alt'
  const [activeFocus, setActiveFocus] = useState(0);
  const [hasScrolled, setHasScrolled] = useState(false);

  // main scene load overlay
  const [loadProgress, setLoadProgress] = useState(0);
  const [mainLoaded, setMainLoaded] = useState(false);

  // responsive & input
  const [isMobile, setIsMobile] = useState(false);

  // normalized (-1..1) for 3D parallax; plus raw pixel pointer for UI repulsion
  const [parallax, setParallax] = useState({ x: 0, y: 0 });
  const [pointer, setPointer] = useState({ x: 0, y: 0 });

  function blurOnTap(e) {
    // prevents iOS from leaving the button focused
    e.currentTarget.blur();
  }

  // mobile detection
  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth <= 768);
    check();
    window.addEventListener("resize", check, { passive: true });
    return () => window.removeEventListener("resize", check);
  }, []);

  // pointer + touch to drive parallax/pointer
  useEffect(() => {
    const handlePointerMove = (e) => {
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      const p = toNormPointer(e.clientX, e.clientY, w, h);
      setParallax(p);
      setPointer({ x: e.clientX, y: e.clientY });
    };

    const handleTouchMove = (e) => {
      if (!e.touches?.[0]) return;
      const touch = e.touches[0];
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;
      const p = toNormPointer(touch.clientX, touch.clientY, w, h);
      setParallax(p);
      setPointer({ x: touch.clientX, y: touch.clientY });
    };

    window.addEventListener("mousemove", handlePointerMove, { passive: true });
    window.addEventListener("touchmove", handleTouchMove, { passive: true });

    return () => {
      window.removeEventListener("mousemove", handlePointerMove);
      window.removeEventListener("touchmove", handleTouchMove);
    };
  }, []);

  // blurbs driven by active focus
  const blurb = FOCUS_BLURBS[activeFocus];
  const hasBlurb = !!blurb;
  const side = blurb?.side || "left";

  const explorarSide = useMemo(() => {
    if (!hasBlurb) return "center";
    return side === "left" ? "right" : "left";
  }, [hasBlurb, side]);

  useEffect(() => {
    if (activeFocus >= 1) setHasScrolled(true);
  }, [activeFocus]);

  const showLeftArrow  = mode === "alt";
  const showRightArrow = mode === "main" && activeFocus === 0 && hasScrolled;

  // device-dependent camera settings (kept identical to your current values)
  const camOffsets = useMemo(
    () =>
      isMobile
        ? [
            [-2, 2.5, 2],
            [2, 2, 2],
            [8, 10, 8],
            [8, 8, 8],
          ]
        : [
            [-2, 2.5, 2],
            [4, 4, 2],
            [7, 10, 8],
            [4, 4, 2],
          ],
    [isMobile]
  );

  const scales = useMemo(
    () => (isMobile ? [0.6, 1.4, 1.6, 1.6] : [0.5, 0.8, 0.9, 0.9]),
    [isMobile]
  );

  // About card transform derived from pointer (avoids recalculating inline)
  const aboutTf = useMemo(() => computeAboutCardTransform(pointer), [pointer]);

  // Navigation
  const goRight = useCallback(() => {
    setMode((prev) => MODE_ORDER_RIGHT[prev] || "main");
  }, []);

  const goLeft = useCallback(() => {
    setMode((prev) => MODE_ORDER_LEFT[prev] || "main");
  }, []);

  // Shared style fragments (keeps JSX readable)
  const rootStyle = useMemo(
    () => ({
      width: "100vw",
      height: "100vh",
      position: "relative",
      overflow: "hidden",
    }),
    []
  );

  const blurbStyle = useMemo(() => {
    if (isMobile) {
      return {
        position: "absolute",
        bottom: "5vh",
        left: "5vw",
        right: "5vw",
        zIndex: 3,
        opacity: hasBlurb ? 1 : 0,
        pointerEvents: hasBlurb ? "auto" : "none",
        transition: "opacity 0.35s ease, transform 0.35s ease",
        willChange: "opacity, transform",
      };
    }

    return {
      position: "absolute",
      top: "50%",
      transform: `translateY(-50%) translateX(${
        hasBlurb ? "0" : side === "left" ? "-20px" : "20px"
      })`,
      left: side === "left" ? "5vh" : "auto",
      right: side === "right" ? "5vh" : "auto",
      width: "30vw",
      maxWidth: "600px",
      zIndex: 2,
      opacity: hasBlurb ? 1 : 0,
      pointerEvents: hasBlurb ? "auto" : "none",
      transition: "opacity 0.35s ease, transform 0.35s ease",
      willChange: "opacity, transform",
    };
  }, [isMobile, hasBlurb, side]);

  const aboutCardStyle = useMemo(
    () => ({
      transform: `
        translate(-50%, -50%)
        translate3d(${aboutTf.offsetX}px, ${aboutTf.offsetY}px, 0)
        rotateX(${aboutTf.tiltX}deg)
        rotateY(${aboutTf.tiltY}deg)
      `,
    }),
    [aboutTf]
  );

  return (
    <div className={`app-root mode-${mode}`} style={rootStyle}>
      {/* STRIPE MASK */}
      <div className="stripe-wrap">
        <StripeMask opacity={1} />
      </div>

      {/* MAIN PAGE */}
      <div className="main-layer" style={{ pointerEvents: "auto" }}>
        <Canvas shadows dpr={[1, 1.5]} gl={{ antialias: false }}>
          <React.Suspense fallback={null}>
            <MainSceneWithProgress
              onReady={() => setMainLoaded(true)}
              onProgress={setLoadProgress}
              scales={scales}
              camOffsets={camOffsets}
              setActiveFocus={setActiveFocus}
              spriteFocusRef={spriteFocusRef}
              chipFocusRef={chipFocusRef}
              biteFocusRef={biteFocusRef}
              sceneGroupRef={sceneGroupRef}
            />
          </React.Suspense>
        </Canvas>

        {/* BLURB PANEL */}
        {blurb && (
          <div className={`blurb-panel blurb-${side}`} style={blurbStyle}>
            <div className="blurb-card">
              <h2 className="blurb-title">{blurb.title}</h2>
              <p className="blurb-body">{blurb.body}</p>
              {blurb.link && (
                <a className="blurb-link" href={blurb.link}>
                  View Here⠀
                </a>
              )}
            </div>
          </div>
        )}

        {/* EXPLORAR TEXT */}
        {(!isMobile || activeFocus === 0) && (
          <div
            className={`explorar-slot explorar-${explorarSide} ${
              isMobile ? "explorar-mobile" : ""
            }`}
            style={{ pointerEvents: "none" }}
          >
              <ParallaxTextPunchout
                text="EXPLORAR"
                fontSize={isMobile ? "18vh" : "20vw"}
              />
          </div>
        )}
      </div>

      {/* ABOUT PAGE */}
      <div className="about-layer">
        <Canvas shadows dpr={[1, 1.5]} gl={{ antialias: false, stencil: true }}>
          <AboutMe parallax={parallax} pointer={pointer} />
        </Canvas>

        <div className="contact-info">
          <div className="contact-info-header">Contact</div>
          <p>
            Email:
            &#97;&#114;&#116;&#104;&#117;&#114;&#115;&#98;&#111;&#98;&#114;&#101;&#107;&#64;&#111;&#117;&#116;&#108;&#111;&#111;&#107;&#46;&#99;&#111;&#109;
          </p>
        </div>
      </div>

      {/* LOADING OVERLAY */}
      <div className={`loading-overlay ${mainLoaded ? "loading-overlay--hidden" : ""}`}>
        <div className="loading-inner">
          <div className="loading-star" style={{ "--load-progress": `${loadProgress}%` }}>
            <img src={`${BASE}starLogo.svg`} alt="Loading" className="loading-star-outline" />
            <div className="loading-star-fill" />
          </div>
          <div className="loading-label">{Math.round(loadProgress)}%</div>
        </div>
      </div>

      {/* ARROWS */}
      {showLeftArrow && (
        <button
          type="button"
          className="mode-arrow mode-arrow-left"
          aria-label="Previous section"
          onClick={goLeft}
          style={{ pointerEvents: "auto", zIndex: 60 }}
        >
          <svg className="mode-arrow-icon" viewBox="-6 -6 83.63 130.36" aria-hidden="true">
            <path d="M 71.63 58.99 c 0 -3.22 -1.25 -6.14 -3.27 -8.33 h 0 s -0.19 -0.19 -0.19 -0.19 c -0.11 -0.11 -0.22 -0.22 -0.33 -0.33 L 21.27 3.55 l -0.02 0.02 C 19.03 1.36 15.97 0 12.6 0 C 5.8 0 0.29 5.51 0.29 12.3 c 0 3.55 1.51 6.73 3.91 8.98 l 37.73 37.73 L 3.58 97.37 h 0 c -2.22 2.23 -3.59 5.3 -3.59 8.69 c 0 6.79 5.51 12.3 12.3 12.3 c 3.47 0 6.6 -1.44 8.84 -3.76 l 46.94 -46.94 l -0.02 -0.02 c 2.2 -2.22 3.57 -5.28 3.57 -8.66 Z" />
          </svg>
        </button>
      )}
      {showRightArrow && (
        <button
          type="button"
          className="mode-arrow mode-arrow-right"
          aria-label="Next section"
          onClick={goRight}
          style={{ pointerEvents: "auto", zIndex: 60 }}
        >
          <svg className="mode-arrow-icon" viewBox="-6 -6 83.63 130.36" aria-hidden="true">
            <path d="M 71.63 58.99 c 0 -3.22 -1.25 -6.14 -3.27 -8.33 h 0 s -0.19 -0.19 -0.19 -0.19 c -0.11 -0.11 -0.22 -0.22 -0.33 -0.33 L 21.27 3.55 l -0.02 0.02 C 19.03 1.36 15.97 0 12.6 0 C 5.8 0 0.29 5.51 0.29 12.3 c 0 3.55 1.51 6.73 3.91 8.98 l 37.73 37.73 L 3.58 97.37 h 0 c -2.22 2.23 -3.59 5.3 -3.59 8.69 c 0 6.79 5.51 12.3 12.3 12.3 c 3.47 0 6.6 -1.44 8.84 -3.76 l 46.94 -46.94 l -0.02 -0.02 c 2.2 -2.22 3.57 -5.28 3.57 -8.66 Z" />
          </svg>
        </button>
      )}
    </div>
  );
}
